import { useEffect, useRef, useState, type ReactNode } from "react";
import { postJson } from "../api";
import { when } from "../ai/ai";
import { useAiData, useTitle } from "../ai/parts";
import {
  Counter,
  DataTable,
  FilterRow,
  Indicator,
  Indicators,
  IndicatorSkeleton,
  MobileActions,
  Pill,
  RowsSkeleton,
  useFilters,
  Window,
} from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { paintTheme, useApp, type Theme } from "../appContext";
import { ConfirmWindow } from "../orgs/PersonActions";
import { dateIn, initials, personHue } from "../orgs/organizations";
import { deviceName } from "../orgs/security";
import { formatText, useTexts, type Locale } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, OptionCards, PageHead, Switch, TextField, useToast } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState, PasswordField } from "../ui/pieces";
import {
  activityDetail,
  activityText,
  failedNotice,
  normalizeActivityFilter,
  securityTabs,
  type ActivityRow,
  type SecurityTab,
} from "./account";
import styles from "./account.module.css";

// Mi cuenta (R7, especificación §5.11): Perfil, Preferencias y Seguridad con sus 5 pestañas, siempre sobre la cuenta de
// la sesión. Se llega desde el menú del avatar (D2). Cada pantalla: esqueleto de la misma vista (C22), «no disponible»
// con «Reintentar» si Identity no responde (nunca 0 ni vacío, H-3) y textos en es-PE y en-US.

const LIMA = "America/Lima";

/** La lectura falló: C40 con «Reintentar» bajo el encabezado de la pantalla. */
function Failed({ head, retry }: { head: ReactNode; retry: () => void }) {
  const { L } = useTexts();
  return (
    <PageState
      head={head}
      title={L("ADM_UNAVAILABLE")}
      line={L("ADM_UNAVAILABLE_LINE")}
      action={
        <button type="button" className="tm-btn secondary" onClick={retry}>
          {L("ADM_RETRY")}
        </button>
      }
    />
  );
}

/** Campo con su error por clave (el error se borra en cuanto el valor es válido, E81). */
type Errors = Record<string, string | undefined>;

// ---------------------------------------------------------------------------------------------------------------------
// Perfil

type Profile = {
  userId: number;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  role: string | null;
};

const roleKey = (role: string | null) =>
  role === "PLATFORM_ADMIN" ? "PLATFORM_ROLE_ADMIN" : role === "PLATFORM_SELLER" ? "PLATFORM_ROLE_SELLER" : null;

/** `cta-perfil` · Mi cuenta › Perfil: la persona (correo y rol como datos) y Nombre y Apellido editables. */
export function ProfilePage() {
  const { L } = useTexts();
  useTitle(L("ACCOUNT_MY_PROFILE"));
  const data = useAiData<Profile>("/api/admin/account/profile");
  const head = <PageHead title={L("ACCOUNT_MY_PROFILE")} />;
  if (data.state === "loading")
    return (
      <>
        {head}
        <RowsSkeleton rows={1} />
        <div className={styles.formSkeleton} aria-hidden="true">
          <span className={ui.bone} />
          <span className={ui.bone} />
        </div>
      </>
    );
  if (!data.data) return <Failed head={head} retry={data.retry} />;
  return <ProfileForm key={data.data.email} profile={data.data} onSaved={() => void data.reload()} />;
}

function ProfileForm({ profile, onSaved }: { profile: Profile; onSaved: () => void }) {
  const { L } = useTexts();
  const toast = useToast();
  const app = useApp();
  const [first, setFirst] = useState(profile.firstName);
  const [last, setLast] = useState(profile.lastName);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const role = roleKey(profile.role);
  const check = (field: "firstName" | "lastName", value: string): string | undefined => {
    const text = value.trim();
    if (!text) return L(field === "firstName" ? "FEEDBACK_FIRST_REQUIRED" : "FEEDBACK_LAST_REQUIRED");
    if (text.length > 100) return L("ADM_ACC_NAME_LIMIT");
    return undefined;
  };
  const save = async () => {
    const found: Errors = { firstName: check("firstName", first), lastName: check("lastName", last) };
    setErrors(found);
    if (found.firstName || found.lastName) {
      requestAnimationFrame(() => document.querySelector<HTMLElement>("main [aria-invalid='true']")?.focus());
      return;
    }
    if (first.trim() === profile.firstName && last.trim() === profile.lastName) {
      toast.show(L("ADM_ACC_NO_CHANGES"));
      return;
    }
    setBusy(true);
    const reply = await postJson<{
      saved?: boolean;
      messageKey?: string;
      fields?: { field: string; messageKey: string }[];
    }>("/api/admin/account/profile", { firstName: first.trim(), lastName: last.trim() });
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      toast.show(L("ADM_ACC_PROFILE_SAVED"));
      onSaved();
      void app.refreshSession();
      return;
    }
    // Nunca se pierde lo escrito: el error va al campo o arriba.
    if (reply.body?.fields?.length)
      setErrors(Object.fromEntries(reply.body.fields.map((item) => [item.field, L(item.messageKey)])));
    else toast.show(L(reply.body?.messageKey ?? "PROFILE_UPDATE_FAILED"));
  };
  const action = (
    <button
      type="button"
      className="tm-btn primary"
      onClick={() => void save()}
      disabled={busy}
      aria-busy={busy || undefined}
    >
      {L("ADM_AI_SAVE")}
    </button>
  );
  return (
    <>
      <PageHead title={L("ACCOUNT_MY_PROFILE")}>
        <span className={ui.desktopOnly}>{action}</span>
      </PageHead>
      <div className={styles.person} data-part="persona">
        <span
          className={`${styles.avatar} npa-person-tone`}
          style={{ ["--person-hue" as string]: String(personHue(profile.userId || profile.displayName)) }}
          aria-hidden="true"
        >
          {initials(profile.displayName)}
        </span>
        <span className={styles.personText}>
          <span className={styles.personName}>
            <strong>{profile.displayName}</strong>
            {role ? <span className={ui.tagNeutral}>{L(role)}</span> : null}
          </span>
          <span>{profile.email}</span>
        </span>
      </div>
      <form
        className={styles.grid}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <TextField
          id="account-first"
          label={L("ACCOUNT_FIRST_NAME")}
          value={first}
          maxLength={100}
          autoComplete="given-name"
          help={L("ADM_ACC_NAME_HELP")}
          error={errors.firstName}
          onChange={(value) => {
            setFirst(value);
            if (errors.firstName) setErrors((current) => ({ ...current, firstName: check("firstName", value) }));
          }}
        />
        <TextField
          id="account-last"
          label={L("ACCOUNT_LAST_NAME")}
          value={last}
          maxLength={100}
          autoComplete="family-name"
          help={L("ADM_ACC_NAME_HELP")}
          error={errors.lastName}
          onChange={(value) => {
            setLast(value);
            if (errors.lastName) setErrors((current) => ({ ...current, lastName: check("lastName", value) }));
          }}
        />
        <button type="submit" hidden />
      </form>
      <MobileActions>{action}</MobileActions>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Preferencias

type Preferences = { theme: Theme; locale: Locale };

/**
 * `cta-preferencias` · Apariencia (Sistema · Claro · Oscuro) e Idioma. El tema se ve al instante al elegirlo (decisión del
 * dueño, 2026-10-31) y vuelve al guardado si se sale sin guardar; el idioma se aplica al guardar, sin recargar.
 */
export function PreferencesPage() {
  const { L } = useTexts();
  useTitle(L("ACCOUNT_PREFERENCES"));
  const data = useAiData<Preferences>("/api/admin/account/preferences");
  const head = <PageHead title={L("ACCOUNT_PREFERENCES")} />;
  if (data.state === "loading")
    return (
      <>
        {head}
        <div className={styles.formSkeleton} aria-hidden="true">
          <span className={ui.bone} />
          <span className={ui.bone} />
        </div>
      </>
    );
  if (!data.data) return <Failed head={head} retry={data.retry} />;
  return <PreferencesForm saved={data.data} onSaved={() => void data.reload()} />;
}

function PreferencesForm({ saved, onSaved }: { saved: Preferences; onSaved: () => void }) {
  const { L } = useTexts();
  const toast = useToast();
  const app = useApp();
  const [theme, setTheme] = useState<Theme>(saved.theme);
  const [locale, setLocale] = useState<Locale>(saved.locale);
  const [busy, setBusy] = useState(false);
  // Tema guardado: al salir de la pantalla sin guardar se vuelve a pintar este, no la vista previa.
  const savedTheme = useRef<Theme>(saved.theme);
  useEffect(() => {
    savedTheme.current = saved.theme;
  }, [saved.theme]);
  useEffect(() => () => paintTheme(savedTheme.current), []);
  const chooseTheme = (value: Theme) => {
    setTheme(value);
    paintTheme(value);
  };
  const save = async () => {
    if (theme === saved.theme && locale === saved.locale) {
      toast.show(L("ADM_ACC_NO_CHANGES"));
      return;
    }
    setBusy(true);
    const reply = await postJson<{ saved?: boolean; messageKey?: string; theme?: Theme; locale?: Locale }>(
      "/api/admin/account/preferences",
      { theme, locale },
    );
    if (reply.status !== 200 || !reply.body?.saved) {
      setBusy(false);
      toast.show(L(reply.body?.messageKey ?? "PREF_SAVE_FAILED"));
      return;
    }
    // Tema e idioma se aplican ya: los textos llegan en el idioma nuevo y el aviso se dice en él.
    savedTheme.current = reply.body.theme ?? theme;
    const next = await app.applyPreferences(reply.body.locale ?? locale, reply.body.theme ?? theme);
    setBusy(false);
    toast.show(next("ADM_ACC_PREFERENCES_SAVED"));
    onSaved();
  };
  const action = (
    <button
      type="button"
      className="tm-btn primary"
      onClick={() => void save()}
      disabled={busy}
      aria-busy={busy || undefined}
    >
      {L("ADM_AI_SAVE")}
    </button>
  );
  return (
    <>
      <PageHead title={L("ACCOUNT_PREFERENCES")}>
        <span className={ui.desktopOnly}>{action}</span>
      </PageHead>
      <div className={styles.stack}>
        <div className={styles.group}>
          <span className={styles.groupTitle} id="pref-theme">
            {L("PREFERENCES_APPEARANCE")}
          </span>
          <div className={styles.cards}>
            <OptionCards
              labelledBy="pref-theme"
              name="theme"
              value={theme}
              onChange={(value) => chooseTheme(value as Theme)}
              options={[
                { value: "SYSTEM", title: L("THEME_SYSTEM"), line: L("ADM_ACC_THEME_SYSTEM_LINE") },
                { value: "LIGHT", title: L("THEME_LIGHT"), line: L("ADM_ACC_THEME_LIGHT_LINE") },
                { value: "DARK", title: L("THEME_DARK"), line: L("ADM_ACC_THEME_DARK_LINE") },
              ]}
            />
          </div>
        </div>
        <div className={styles.group}>
          <span className={styles.groupTitle} id="pref-locale">
            {L("PREFERENCES_LANGUAGE")}
          </span>
          <div className="tm-pills" role="radiogroup" aria-labelledby="pref-locale">
            {(
              [
                ["es-PE", "Español (Perú)"],
                ["en-US", "English (United States)"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                className={`tm-pill ${ui.pill}`}
                lang={value === "en-US" ? "en" : "es"}
                aria-checked={locale === value ? "true" : "false"}
                aria-pressed={locale === value ? "true" : "false"}
                onClick={() => setLocale(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <MobileActions>{action}</MobileActions>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Seguridad: encabezado y pestañas

function SecurityHead({ current, action }: { current: SecurityTab; action?: ReactNode }) {
  const { L } = useTexts();
  return (
    <>
      <PageHead title={L("ACCOUNT_SECURITY")}>{action}</PageHead>
      <nav className={`tm-tabs ${ui.tabs}`} aria-label={L("ACCOUNT_SECURITY")} data-part="pestanas">
        {securityTabs.map((tab) => (
          <a key={tab.key} href={tab.href} aria-current={tab.key === current ? "page" : undefined}>
            {L(tab.labelKey)}
          </a>
        ))}
      </nav>
    </>
  );
}

type SecuritySummary = {
  mfaEnabled: boolean | null;
  trustedDevices: number | null;
  sessions: number | null;
  sessionDevices: number | null;
  lastSignInAt: string | null;
  failedSinceLastAccess: number | null;
  passwordChangedAt: string | null;
  activityAvailable: boolean;
};

/** `cta-seguridad` · Resumen: aviso de intentos fallidos con «Ver actividad» e indicadores que llevan a cada pestaña. */
export function SecurityPage() {
  const { L, locale } = useTexts();
  useTitle(L("ACCOUNT_SECURITY"));
  const data = useAiData<SecuritySummary>("/api/admin/account/security");
  const head = <SecurityHead current="summary" />;
  if (data.state === "loading")
    return (
      <>
        {head}
        <IndicatorSkeleton count={4} label={L("ACCOUNT_SECURITY")} />
      </>
    );
  if (!data.data) return <Failed head={head} retry={data.retry} />;
  const s = data.data;
  const unavailable = L("ADM_UNAVAILABLE");
  return (
    <>
      {head}
      {s.failedSinceLastAccess ? (
        <Notice
          tone="warning"
          action={
            <a className="tm-btn secondary" href="/Account/Security/Activity?filter=signins">
              {L("ADM_ACC_SEE_ACTIVITY")}
            </a>
          }
        >
          {failedNotice(L, s.failedSinceLastAccess)}
        </Notice>
      ) : null}
      <Indicators label={L("ACCOUNT_SECURITY")} count={4}>
        <Indicator
          label={L("ADM_ACC_TAB_PASSWORD")}
          small
          value={
            s.activityAvailable ? (s.passwordChangedAt ? dateIn(locale, s.passwordChangedAt, LIMA, false) : "—") : "—"
          }
          muted={!s.passwordChangedAt}
          line={
            s.activityAvailable ? L(s.passwordChangedAt ? "ADM_ACC_LAST_CHANGE" : "ADM_ACC_NEVER_CHANGED") : unavailable
          }
          href="/Account/Security/Password"
        />
        <Indicator
          label={L("ADM_ACC_TAB_MFA")}
          small
          value={s.mfaEnabled == null ? "—" : L(s.mfaEnabled ? "ADM_ACC_MFA_ON" : "ADM_ACC_MFA_OFF")}
          muted={s.mfaEnabled == null}
          line={
            s.trustedDevices == null
              ? unavailable
              : formatText(L(s.trustedDevices === 1 ? "ADM_ACC_TRUSTED_ONE" : "ADM_ACC_TRUSTED"), s.trustedDevices)
          }
          href="/Account/Security/Mfa"
        />
        <Indicator
          label={L("ADM_ACC_TAB_SESSIONS")}
          value={s.sessions == null ? "—" : s.sessions.toLocaleString(locale)}
          muted={s.sessions == null}
          line={
            s.sessionDevices == null
              ? unavailable
              : formatText(
                  L(s.sessionDevices === 1 ? "ADM_ACC_IN_DEVICES_ONE" : "ADM_ACC_IN_DEVICES"),
                  s.sessionDevices,
                )
          }
          href="/Account/Security/Sessions"
        />
        <Indicator
          label={L("ADM_ACC_TAB_ACTIVITY")}
          small
          value={s.activityAvailable ? when(L, locale, s.lastSignInAt) : "—"}
          muted={!s.lastSignInAt}
          line={s.activityAvailable ? L("ADM_ACC_LAST_ACCESS") : unavailable}
          href="/Account/Security/Activity"
        />
      </Indicators>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Contraseña

/** `cta-pwd` · al cambiarla se cierran las sesiones y se vuelve a Ingreso con «Tu contraseña cambió». */
export function PasswordPage() {
  const { L } = useTexts();
  useTitle(`${L("ACCOUNT_SECURITY")} · ${L("ADM_ACC_TAB_PASSWORD")}`);
  const toast = useToast();
  const [values, setValues] = useState({ current: "", next: "", confirm: "" });
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const check = (field: "current" | "next" | "confirm", draft: typeof values): string | undefined => {
    if (field === "current") return draft.current ? undefined : L("LOGIN_PASSWORD_REQUIRED");
    if (field === "next") {
      if (!draft.next) return L("LOGIN_PASSWORD_REQUIRED");
      return draft.next.length < 12 || draft.next.length > 200 ? L("PWD_LENGTH_RULE") : undefined;
    }
    return draft.next && draft.confirm !== draft.next ? L("PWD_ERR_MISMATCH") : undefined;
  };
  const update = (field: "current" | "next" | "confirm", value: string) => {
    const draft = { ...values, [field]: value };
    setValues(draft);
    // E81: el error de un campo se va en cuanto el valor es válido (y la repetición sigue a la nueva).
    setErrors((current) => ({
      ...current,
      ...(current[field] ? { [field]: check(field, draft) } : {}),
      ...(field === "next" && current.confirm ? { confirm: check("confirm", draft) } : {}),
    }));
  };
  const submit = async () => {
    const found: Errors = {
      current: check("current", values),
      next: check("next", values),
      confirm: check("confirm", values),
    };
    setErrors(found);
    if (found.current || found.next || found.confirm) {
      requestAnimationFrame(() => document.querySelector<HTMLElement>("main [aria-invalid='true']")?.focus());
      return;
    }
    setBusy(true);
    const reply = await postJson<{
      status: string;
      next?: string;
      messageKey?: string;
      fields?: { field: string; messageKey: string }[];
    }>("/api/admin/account/change-password", { current: values.current, new: values.next, confirm: values.confirm });
    if (reply.body?.status === "changed") {
      window.location.assign(reply.body.next ?? "/login?changed=1");
      return;
    }
    setBusy(false);
    if (reply.body?.fields?.length) {
      setErrors(
        Object.fromEntries(
          reply.body.fields.map((item) => [item.field === "new" ? "next" : item.field, L(item.messageKey)]),
        ),
      );
      requestAnimationFrame(() => document.querySelector<HTMLElement>("main [aria-invalid='true']")?.focus());
    } else toast.show(L(reply.body?.messageKey ?? "PWD_ERR_GENERIC"));
  };
  const action = (
    <button
      type="button"
      className="tm-btn primary"
      onClick={() => void submit()}
      disabled={busy}
      aria-busy={busy || undefined}
    >
      {L("ADM_ACC_CHANGE_PASSWORD")}
    </button>
  );
  return (
    <>
      <SecurityHead current="password" action={<span className={ui.desktopOnly}>{action}</span>} />
      <Notice tone="info">{L("ADM_ACC_PASSWORD_NOTICE")}</Notice>
      <form
        className={styles.grid}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <PasswordField
          id="pwd-current"
          label={L("PWD_CURRENT")}
          value={values.current}
          autoComplete="current-password"
          error={errors.current}
          onChange={(value) => update("current", value)}
        />
        <span className={styles.gridGap} aria-hidden="true" />
        <PasswordField
          id="pwd-new"
          label={L("PWD_NEW")}
          value={values.next}
          autoComplete="new-password"
          help={L("ADM_ACC_PASSWORD_HELP")}
          error={errors.next}
          onChange={(value) => update("next", value)}
        />
        <PasswordField
          id="pwd-confirm"
          label={L("ADM_ACC_PASSWORD_REPEAT")}
          value={values.confirm}
          autoComplete="new-password"
          error={errors.confirm}
          onChange={(value) => update("confirm", value)}
        />
        <button type="submit" hidden />
      </form>
      <MobileActions>{action}</MobileActions>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Verificación en dos pasos

type Mfa = {
  enabled: boolean;
  maskedEmail: string | null;
  devices: { deviceId: number; device: string | null; browser: string | null; os: string | null; createdAt: string }[];
};

/** `cta-mfa` · interruptor (activar con la ventana de pasos; desactivar confirma) y dispositivos de confianza. */
export function MfaPage() {
  const { L, locale } = useTexts();
  useTitle(`${L("ACCOUNT_SECURITY")} · ${L("ADM_ACC_TAB_MFA")}`);
  const toast = useToast();
  const data = useAiData<Mfa>("/api/admin/account/mfa");
  const [dialog, setDialog] = useState<"enable" | "disable" | null>(null);
  const [revoking, setRevoking] = useState<number | null>(null);
  const head = <SecurityHead current="mfa" />;
  if (data.state === "loading")
    return (
      <>
        {head}
        <RowsSkeleton rows={3} />
      </>
    );
  if (!data.data) return <Failed head={head} retry={data.retry} />;
  const mfa = data.data;
  const revoke = async (deviceId: number) => {
    setRevoking(deviceId);
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/account/mfa/devices/${deviceId}/revoke`,
      {},
    );
    setRevoking(null);
    if (reply.status !== 200 || !reply.body?.saved) {
      toast.show(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
      return;
    }
    toast.show(L("ADM_ACC_DEVICE_REVOKED"));
    void data.reload();
  };
  return (
    <>
      {head}
      <div className={styles.switchRow}>
        <Switch
          title={L("ADM_ACC_TAB_MFA")}
          line={
            mfa.enabled
              ? mfa.maskedEmail
                ? formatText(L("ADM_ACC_MFA_ON_LINE"), mfa.maskedEmail)
                : L("ADM_ACC_MFA_ON_LINE_NO_EMAIL")
              : L("ADM_ACC_MFA_OFF_LINE")
          }
          checked={mfa.enabled}
          onChange={(value) => setDialog(value ? "enable" : "disable")}
        />
      </div>
      <section className={styles.section} aria-labelledby="mfa-devices">
        <h2 id="mfa-devices" className={ui.subtitle}>
          {L("ADM_ACC_TRUSTED_TITLE")}
        </h2>
        <p className={ui.sectionLine}>{L("ADM_ACC_TRUSTED_LINE")}</p>
        {mfa.devices.length === 0 ? (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_ACC_NO_TRUSTED")}</strong>
            <span>{L("ADM_ACC_NO_TRUSTED_LINE")}</span>
          </div>
        ) : (
          <ul className={`tm-list ${ui.list}`} aria-labelledby="mfa-devices">
            {mfa.devices.map((device) => (
              <li key={device.deviceId} className={ui.row}>
                <span className={ui.mark} aria-hidden="true">
                  <ChatIcon name="key-round" />
                </span>
                <span className={ui.rowText}>
                  <span className={ui.line1}>
                    <strong>{deviceName(L, device)}</strong>
                  </span>
                  <span className={ui.line2}>
                    {formatText(L("ADM_ACC_SINCE"), dateIn(locale, device.createdAt, LIMA, false))}
                  </span>
                </span>
                <span className={ui.rowEnd}>
                  <button
                    type="button"
                    className="tm-btn secondary"
                    disabled={revoking === device.deviceId}
                    aria-busy={revoking === device.deviceId || undefined}
                    aria-label={`${L("SEC_REVOKE")}: ${deviceName(L, device)}`}
                    onClick={() => void revoke(device.deviceId)}
                  >
                    {L("SEC_REVOKE")}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <ConfirmWindow
        open={dialog === "disable"}
        title={L("ADM_ACC_MFA_DISABLE_TITLE")}
        line={L("ADM_ACC_MFA_DISABLE_LINE")}
        action={L("ADM_ACC_MFA_DISABLE")}
        onClose={() => setDialog(null)}
        onConfirm={async () => {
          const reply = await postJson<{ saved?: boolean; messageKey?: string }>("/api/admin/account/mfa/disable", {});
          if (reply.status !== 200 || !reply.body?.saved) {
            toast.show(L(reply.body?.messageKey ?? "MFA_ERROR"));
            return false;
          }
          toast.show(L("ADM_ACC_MFA_DISABLED"));
          void data.reload();
          return true;
        }}
      />
      {dialog === "enable" ? (
        <EnableMfaWindow
          maskedEmail={mfa.maskedEmail}
          onClose={() => setDialog(null)}
          onDone={() => {
            toast.show(L("ADM_ACC_MFA_ENABLED"));
            void data.reload();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Activar la verificación en dos pasos (ventana con pasos, como hoy en dos pasos): 1 · enviar el código al correo;
 * 2 · escribir el código y activarla. Solo se activa con un código válido. El código de desarrollo se ve solo en DEV.
 */
function EnableMfaWindow({
  maskedEmail,
  onClose,
  onDone,
}: {
  maskedEmail: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { L } = useTexts();
  const [challenge, setChallenge] = useState<{ id: number; devCode: string | null } | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    setError(null);
    const reply = await postJson<{
      saved?: boolean;
      challengeId?: number;
      devCode?: string | null;
      messageKey?: string;
    }>("/api/admin/account/mfa/challenge", {});
    setBusy(false);
    if (reply.status !== 200 || !reply.body?.saved || !reply.body.challengeId) {
      setError(L(reply.body?.messageKey ?? "MFA_ERROR"));
      return;
    }
    setChallenge({ id: reply.body.challengeId, devCode: reply.body.devCode ?? null });
    requestAnimationFrame(() => document.getElementById("mfa-code")?.focus());
  };
  const verify = async () => {
    if (!code.trim()) {
      setError(L("ADM_ACC_MFA_CODE_REQUIRED"));
      return;
    }
    setBusy(true);
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>("/api/admin/account/mfa/verify", {
      challengeId: challenge!.id,
      code: code.trim(),
    });
    setBusy(false);
    if (reply.status !== 200 || !reply.body?.saved) {
      setError(L(reply.body?.messageKey ?? "MFA_ERROR"));
      return;
    }
    onDone();
    onClose();
  };
  return (
    <Window
      open
      title={L("ADM_ACC_MFA_ENABLE_TITLE")}
      onClose={() => !busy && onClose()}
      steps={{ labels: [L("ADM_ACC_MFA_STEP_SEND"), L("ADM_ACC_MFA_STEP_CODE")], current: challenge ? 1 : 0 }}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          {challenge ? (
            <button
              type="button"
              className="tm-btn primary"
              onClick={() => void verify()}
              disabled={busy}
              aria-busy={busy || undefined}
            >
              {L("ADM_ACC_MFA_ACTIVATE")}
            </button>
          ) : (
            <button
              type="button"
              className="tm-btn primary"
              onClick={() => void send()}
              disabled={busy}
              aria-busy={busy || undefined}
            >
              {L("MFA_SEND_CODE")}
            </button>
          )}
        </>
      }
    >
      {challenge ? (
        <div className="tm-field">
          <p className="tm-confirm-line">{L("MFA_HINT")}</p>
          <label htmlFor="mfa-code">{L("MFA_CODE")}</label>
          <input
            id="mfa-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error ? "mfa-code-error" : challenge.devCode ? "mfa-code-dev" : undefined}
            onChange={(event) => {
              setCode(event.target.value);
              if (error && event.target.value.trim()) setError(null);
            }}
          />
          {error ? (
            <span id="mfa-code-error" className="tm-field-error">
              {error}
            </span>
          ) : null}
          {challenge.devCode ? (
            <small id="mfa-code-dev" className="tm-field-help">
              {formatText(L("ADM_ACC_MFA_DEV_CODE"), challenge.devCode)}
            </small>
          ) : null}
        </div>
      ) : (
        <>
          <p className="tm-confirm-line">
            {maskedEmail ? formatText(L("ADM_ACC_MFA_SEND_TO"), maskedEmail) : L("SEC_MFA_NO_EMAIL")}
          </p>
          {error ? <Notice tone="error">{error}</Notice> : null}
        </>
      )}
    </Window>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Sesiones

type OwnSession = {
  sessionId: string;
  device: string | null;
  browser: string | null;
  os: string | null;
  ip: string | null;
  lastActivityAt: string;
  current: boolean;
};

/** `cta-sesiones` · «Sesión actual» marcada, «Cerrar» en las demás y «Cerrar las demás» con confirmación (C12). */
export function SessionsPage() {
  const { L, locale } = useTexts();
  useTitle(`${L("ACCOUNT_SECURITY")} · ${L("ADM_ACC_TAB_SESSIONS")}`);
  const toast = useToast();
  const data = useAiData<{ items: OwnSession[] }>("/api/admin/account/sessions");
  const [confirming, setConfirming] = useState(false);
  const [closing, setClosing] = useState<string | null>(null);
  const others = data.data?.items.filter((item) => !item.current).length ?? 0;
  const action =
    others > 0 ? (
      <button type="button" className="tm-btn primary" onClick={() => setConfirming(true)}>
        {L("SEC_CLOSE_OTHERS")}
      </button>
    ) : null;
  const head = (
    <SecurityHead current="sessions" action={action ? <span className={ui.desktopOnly}>{action}</span> : undefined} />
  );
  if (data.state === "loading")
    return (
      <>
        <SecurityHead current="sessions" />
        <RowsSkeleton rows={3} />
      </>
    );
  if (!data.data) return <Failed head={<SecurityHead current="sessions" />} retry={data.retry} />;
  const close = async (session: OwnSession) => {
    setClosing(session.sessionId);
    const reply = await postJson<{ saved?: boolean; next?: string; messageKey?: string }>(
      `/api/admin/account/sessions/${session.sessionId}/revoke`,
      {},
    );
    setClosing(null);
    if (reply.status !== 200 || !reply.body?.saved) {
      toast.show(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
      return;
    }
    if (reply.body.next) {
      window.location.assign(reply.body.next);
      return;
    }
    toast.show(L("ADM_ACC_SESSION_CLOSED"));
    void data.reload();
  };
  // RC2 · la fecha es un dato de la línea («Windows · 10.0.0.1 · Hoy 06:18»), no parte de una frase: va como en el resto
  // del Admin, con mayúscula (D12).
  const line = (session: OwnSession) =>
    [
      session.os && session.os !== "UNKNOWN" ? session.os : null,
      session.ip,
      session.current ? L("ADM_ACC_ACTIVE_NOW") : when(L, locale, session.lastActivityAt),
    ]
      .filter(Boolean)
      .join(" · ");
  return (
    <>
      {head}
      <ul className={`tm-list ${ui.list}`} aria-label={L("ADM_ACC_TAB_SESSIONS")} data-part="lista">
        {data.data.items.map((session) => (
          <li key={session.sessionId} className={ui.row}>
            <span className={ui.mark} aria-hidden="true">
              <ChatIcon name="key-round" />
            </span>
            <span className={ui.rowText}>
              <span className={ui.line1}>
                <strong>{deviceName(L, session)}</strong>
                {session.current ? <span className={ui.tagAttention}>{L("SEC_CURRENT_SESSION")}</span> : null}
              </span>
              <span className={ui.line2}>{line(session)}</span>
            </span>
            {session.current ? null : (
              <span className={ui.rowEnd}>
                <button
                  type="button"
                  className="tm-btn secondary"
                  disabled={closing === session.sessionId}
                  aria-busy={closing === session.sessionId || undefined}
                  aria-label={`${L("ADM_ACC_CLOSE")}: ${deviceName(L, session)}`}
                  onClick={() => void close(session)}
                >
                  {L("ADM_ACC_CLOSE")}
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {action ? <MobileActions>{action}</MobileActions> : null}
      <ConfirmWindow
        open={confirming}
        title={L("SEC_SESSIONS_CLOSE_OTHERS_TITLE")}
        line={formatText(L(others === 1 ? "ADM_ACC_CLOSE_OTHERS_LINE_ONE" : "ADM_ACC_CLOSE_OTHERS_LINE"), others)}
        action={L("ADM_ACC_CLOSE_SESSIONS")}
        onClose={() => setConfirming(false)}
        onConfirm={async () => {
          const reply = await postJson<{ saved?: boolean; closed?: number; messageKey?: string }>(
            "/api/admin/account/sessions/revoke-others",
            {},
          );
          void data.reload();
          if (reply.status !== 200 || !reply.body?.saved) {
            toast.show(
              reply.body?.closed
                ? formatText(L("ADM_ACC_CLOSED_PARTIAL"), reply.body.closed)
                : L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"),
            );
            return false;
          }
          const closed = reply.body.closed ?? 0;
          toast.show(formatText(L(closed === 1 ? "ADM_ACC_CLOSED_ONE" : "ADM_ACC_CLOSED"), closed));
          return true;
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Actividad

/** `cta-actividad` · Todo · Ingresos · Seguridad; intentos de ingreso y eventos de seguridad en una tabla paginada. */
export function ActivityPage() {
  const { L, locale } = useTexts();
  useTitle(`${L("ACCOUNT_SECURITY")} · ${L("ADM_ACC_TAB_ACTIVITY")}`);
  const { get, set } = useFilters();
  const filter = normalizeActivityFilter(get("filter"));
  const list = useServerList<ActivityRow>(`/api/admin/account/activity${filter === "all" ? "" : `?filter=${filter}`}`);
  const head = <SecurityHead current="activity" />;
  const what = (row: ActivityRow) => {
    const view = activityText(L, row);
    return view.failed ? <span className="tm-tag npa-tag-error">{view.text}</span> : view.text;
  };
  return (
    <>
      {head}
      <FilterRow label={L("ADM_AI_FILTERS")}>
        {(
          [
            ["all", "SEC_FILTER_ALL"],
            ["signins", "ADM_ACC_FILTER_SIGNINS"],
            ["security", "SEC_FILTER_SECURITY"],
          ] as const
        ).map(([value, key]) => (
          <Pill key={value} pressed={filter === value} onClick={() => set({ filter: value === "all" ? "" : value })}>
            {L(key)}
          </Pill>
        ))}
      </FilterRow>
      {list.state === "loading" ? (
        <RowsSkeleton rows={5} table />
      ) : list.state !== "ready" ? (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={list.reload}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      ) : list.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_ACC_NO_ACTIVITY")}</strong>
          {filter === "all" ? null : (
            <button type="button" className="tm-btn secondary" onClick={() => set({ filter: "" })}>
              {L("ADM_ACC_SEE_ALL_ACTIVITY")}
            </button>
          )}
        </div>
      ) : (
        <>
          <Counter>
            {formatText(
              L(list.total === 1 ? "ADM_ACC_N_EVENTS_ONE" : "ADM_ACC_N_EVENTS"),
              list.total.toLocaleString(locale),
            )}
          </Counter>
          <DataTable<ActivityRow>
            label={L("ADM_ACC_TAB_ACTIVITY")}
            rows={list.items}
            rowKey={(row) => row.key}
            columns={[
              { key: "date", title: L("ADM_ACC_COL_DATE"), render: (row) => when(L, locale, row.occurredAt) },
              { key: "what", title: L("ADM_ACC_COL_WHAT"), render: what },
              { key: "detail", title: L("ADM_ACC_COL_DETAIL"), render: (row) => activityDetail(L, row) },
            ]}
            mobile={(row) => {
              const view = activityText(L, row);
              return {
                title: view.failed ? when(L, locale, row.occurredAt) : view.text,
                tag: view.failed ? <span className="tm-tag npa-tag-error">{view.text}</span> : undefined,
                line: [view.failed ? null : when(L, locale, row.occurredAt), activityDetail(L, row)]
                  .filter(Boolean)
                  .join(" · "),
              };
            }}
          />
          <ListFooter list={list} />
        </>
      )}
    </>
  );
}
